import type { FC, ComponentType, ReactNode } from 'react';
import React, {
	Fragment,
	useState,
	useEffect,
	useRef,
	useContext,
	useLayoutEffect,
	useMemo,
	useCallback,
} from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';
import { styled } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import NewButton from '@atlaskit/button/default/button';
import { ToolbarButton } from '@atlaskit/editor-toolbar/toolbar-button';
import { ToolbarTooltip } from '@atlaskit/editor-toolbar/toolbar-tooltip';
import { CommentIcon } from '@atlaskit/editor-toolbar/comment-icon';
import { DefineIcon } from '@atlaskit/editor-toolbar/define-icon';
import { ToolbarButtonGroup } from '@atlaskit/editor-toolbar/toolbar-button-group';
import { ToolbarSection, SeparatorPosition } from '@atlaskit/editor-toolbar/toolbar-section';
import AddIssue from '@atlaskit/icon/core/add';
import type { TooltipPrimitiveProps } from '@atlaskit/tooltip/TooltipPrimitive';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import TooltipPrimitive from '@atlaskit/tooltip/TooltipPrimitive';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import ModalTransition from '@atlaskit/onboarding/modal-transition';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTransition from '@atlaskit/onboarding/spotlight-transition';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTarget from '@atlaskit/onboarding/spotlight-target';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';

import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics/ai-events-instrumentation-provider';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { RovoChatHighlightActions } from '@atlassian/conversation-assistant-ui-components';
import {
	CONVERSATION_ASSISTANT_CONTAINER_ID,
	CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
} from '@atlassian/conversation-assistant/utils';
import { htmlToADF } from '@atlassian/html-adf-transformer/html-to-adf';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { UNIVERSAL_OPEN_COMMENT_EDITOR } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import {
	READING_AIDS_EXPERIENCE,
	VIEW_PAGE_CONTEXT_MENU_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ACTION_PANEL } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { WebItemLocation } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import { FORGE_MODULE_CONTEXT_MENU } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { LazyForgeUIExtensions } from '@confluence/forge-ui/entry-points/lazy.ts';
import type { ForgeUIExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { ContentBodyContext } from '@confluence/content-body/entry-points/ContentBodyContext.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { useCommentsContentState } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import {
	useReattachComment,
	useReattachCommentDispatch,
} from '@confluence/comment-context/entry-points/ReattachCommentContext.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { LoadableLazy } from '@confluence/loadable';
import { getLogger } from '@confluence/logger';
import { ReadingAidsPopup } from '@confluence/contextual-reading-aids/entry-points/readingAidsPopup.ts';
import { ReadingAidsChangeboardingModal } from '@confluence/contextual-reading-aids/entry-points/ReadingAidsChangeboardingModal.ts';
import { ReadingAidsChangeboardingSpotlight } from '@confluence/contextual-reading-aids/entry-points/ReadingAidsChangeboardingSpotlight.ts';
import { READING_AIDS_CHANGEBOARDING_SPOTLIGHT_ID } from '@confluence/contextual-reading-aids/entry-points/ReadingAidsChangeboardingSpotlightId.ts';
import { READING_AIDS_MAX_WORD_COUNT } from '@confluence/contextual-reading-aids/entry-points/reading-aids-utils.ts';
import { useReadingAidsActions } from '@confluence/contextual-reading-aids/entry-points/ReadingAidsStore.ts';
import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { isErrorMarkedAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { SingleIssueCreateProvidersWrapper } from '@confluence/issue-create-side-panel/entry-points/SingleIssueCreateProvidersWrapper.ts';
import { IssueCreateWarningDialog } from '@confluence/issue-create-side-panel/entry-points/IssueCreateWarningDialog.ts';
import { ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanel.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useOmniBarVisibilityActions } from '@confluence/omni-bar-api/entry-points/OmniBarVisibilityStore.ts';
import { useRendererRemixContext } from '@confluence/malleable-ui-common/entry-points/useRendererRemixContext.ts';
import { InlineReactionAction } from '@confluence/experiment-inline-reactions/entry-points/InlineReactionAction.ts';
import { useIsRemixAvailable } from '@confluence/remix-availability/entry-points/useIsRemixAvailable.ts';

import { HIGHLIGHT_ACTIONS_REMIX_TRIGGER_SELECTOR, RemixAction } from './RemixAction.tsx';
import { getLegacyConvertedSelectionObj } from './getLegacyConvertedSelectionObj.ts';
import { CreateJiraIssueActions } from './CreateJiraIssueActions.tsx';
import { AddToTrelloInboxActions } from './AddToTrelloInboxActions.tsx';
import { prefetchTrelloAccess } from './useTrelloAccess.ts';
import { Popup, type Position } from './Popup.tsx';
import { popupPortalContainerId } from './HighlightActionsProvider.tsx';
import { createJiraIssueSummaryHandle } from './builtInButtonHandlers.ts';
import { HighlightActionsContext } from './HighlightActionsContext.ts';
import {
	isHighlightValidForInlineComment,
	textNormalize,
	getRange,
	findStickyHeader,
	isHighlightValidForBulkCreateJiraIssues,
} from './highlightActionsUtils.ts';
import { OtherActions } from './OtherActions.tsx';
import {
	getCreateButtonDisabledMessage,
	getReactionButtonDisabledMessage,
} from './getCreateButtonDisabledMessage.tsx';
import { DisabledButtonWrapper, DisabledButtonOverlay } from './highlightActionsStyled.tsx';
import type { HighlightActionsContextState } from './HighlightActionsContext.ts';
import type {
	CreateSingleAiJiraIssueHandleParams,
	CreateSingleJiraIssueHandleParams,
} from './createJiraIssueHandlers.ts';
import {
	createSingleAiJiraIssueHandle,
	createSingleJiraIssueHandle,
	getHTMLString,
} from './createJiraIssueHandlers.ts';

const POPUP_MOUSEUP_DISTANCE_THRESHOLD = 100;
const BUILT_IN_ITEMS = [
	'com.atlassian.confluence.plugins.confluence-jira-content:create-JIRA-issue-summary',
	'com.atlassian.confluence.plugins.confluence-trello-content:create-trello-card-quick-capture',
];

export const DROPDOWN_EXCLUDED_ITEMS: string[] = [
	'com.atlassian.confluence.plugins.confluence-inline-comments:create-inline-comment',
	'com.atlassian.confluence.plugins.confluence-jira-content:create-JIRA-issue-summary',
];

const i18n = defineMessages({
	commentTooltip: {
		id: 'highlight-actions.inline.comment.tooltip',
		defaultMessage: 'Comment',
		description: 'Tooltip for the inline comment button in the highlight actions menu',
	},
	defineTooltip: {
		id: 'highlight-actions.define.tooltip',
		defaultMessage: 'Define',
		description:
			'Tooltip for button that opens the reading aids popup, which provides an AI-generated definition for the highlighted term',
	},
});

const logger = getLogger('inline-comment');

const ForgeAction = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ForgeAction" */ './ForgeAction.tsx')).ForgeAction,
});

const DefinitionAction = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-DefinitionAction" */ './DefinitionAction.tsx'))
			.DefinitionAction,
});

function trapFocusInSelectionToolbar(event: KeyboardEvent) {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const highlightActionsToolbar = document.querySelector('#highlightActionsPopup');
	if (highlightActionsToolbar && event.key === 'Tab' && !event.ctrlKey && !event.altKey) {
		event.preventDefault();
		event.stopPropagation();

		// Get all focusable elements in the toolbar
		const elements = Array.from(highlightActionsToolbar.querySelectorAll('button, a')).filter(
			(curr) => {
				if (curr instanceof HTMLButtonElement) {
					return !curr.disabled;
				}
				if (curr instanceof HTMLAnchorElement) {
					return !!curr.href;
				}
				return false;
			},
		);

		// Find the index of the currently focused element
		let idx = event.shiftKey ? elements.length : -1;
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const focusedElement = document.activeElement;
		if (highlightActionsToolbar.contains(focusedElement)) {
			for (let i = 0; i < elements.length; i++) {
				if (elements[i] === focusedElement) {
					idx = i;
					break;
				}
			}
		}

		// Focus the next element to focus
		event.shiftKey ? idx-- : idx++;
		if (idx < 0) {
			idx = elements.length - 1;
		} else if (idx >= elements.length) {
			idx = 0;
		}
		const next = elements[idx];
		if (next && (next instanceof HTMLButtonElement || next instanceof HTMLAnchorElement)) {
			next.focus();
		}
	}
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomLineWrapTooltip = styled(TooltipPrimitive)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	padding: `${token('space.025')} ${token('space.075')}`,
	gap: token('space.100'),

	position: 'relative',

	background: token('color.background.neutral.bold'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),

	color: token('color.text.inverse'),
	font: token('font.body.small'),
	fontWeight: token('font.weight.regular'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomLineWrapTooltipUpdated = styled(TooltipPrimitive)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	padding: `${token('space.025')} ${token('space.075')}`,
	gap: token('space.100'),

	position: 'relative',

	background: token('color.background.neutral.bold'),
	borderRadius: token('radius.small'),

	color: token('color.text.inverse'),
	font: token('font.body.small'),
	fontWeight: token('font.weight.regular'),
});

export type SelectionRect = {
	top: number;
	left: number;
	width: number;
	height: number;
};

export type ActiveSelection = {
	selectedText: string;
	selectedRange: Range;
};

export type HighlightActionsSnippetSlots = {
	button: ReactNode;
	dropdownItem: ReactNode;
};

type HighlightActionsProps = {
	contentId: string;
	children?: ReactNode;
	actions?: any;
	lastModifiedDate?: string | null;
	isArchived?: boolean;
	contentStatusError?: ApolloErrorInterop;
	isAnnotationAllowed?: boolean;
	range?: Range;
	onCommentButtonPress?: () => void;
	onRemixButtonPress?: (event?: React.MouseEvent<HTMLElement>) => void;
	onRemixButtonMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
	remixDisabledReason?: 'archived' | 'version-history';
	isFabric?: boolean;
	contentType?: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed' | 'slide' | 'folder';
	inlineNodeTypes?: string[];
	onCommentButtonMount?: () => void;
	snippetSlots?: HighlightActionsSnippetSlots;
	preloadedCanAddComments?: boolean;
};

type HighlightActionsStates = {
	show: boolean;
	top: number;
	left: number;
	activeSelection: ActiveSelection | null;
	selectionRect: SelectionRect | null;
	forgeExtension: ForgeUIExtensionType | null;
	isReadingAidsOpen: boolean;
	readingAidsChangeboardingStep:
		| 'disabled' // changeboarding is not enabled
		| 'idle' // changeboarding is enabled, but not started
		| 'modal-word-count' // step 1, option a: show modal if word count is over 5
		| 'modal-undefined' // step 1, option b: show modal if text selection is not defineable
		| 'spotlight'; // step 2: show spotlight on "define" button
} & HighlightActionsContextState;

export type CreateIssueRecordedProps =
	| CreateSingleAiJiraIssueHandleParams
	| CreateSingleJiraIssueHandleParams
	| undefined;

type HighlightValidityArgs = {
	content: HTMLDivElement | null;
	isFabric?: boolean;
	isAnnotationAllowed?: boolean;
	contentId: string;
};

const getIsHighlightValid = ({
	content,
	isFabric,
	isAnnotationAllowed,
	contentId,
}: HighlightValidityArgs): boolean => {
	if (!content) {
		return false;
	}

	if (isFabric) {
		return Boolean(isAnnotationAllowed);
	}

	return isHighlightValidForInlineComment(content, contentId);
};

export const HighlightActions: FC<HighlightActionsProps> = ({
	contentId,
	children,
	actions,
	lastModifiedDate,
	isArchived,
	contentStatusError,
	range,
	isAnnotationAllowed,
	onCommentButtonPress,
	onRemixButtonPress,
	onRemixButtonMouseDown,
	remixDisabledReason,
	isFabric,
	contentType,
	inlineNodeTypes,
	onCommentButtonMount,
	snippetSlots,
	preloadedCanAddComments,
}) => {
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const isRemixAvailable = useIsRemixAvailable();

	const [actionStates, setActionStates] = useState<HighlightActionsStates>({
		firstHighlightShown: false,
		show: false,
		top: 0,
		left: 0,
		forgeExtension: null,
		activeSelection: null,
		selectionRect: null,
		isReadingAidsOpen: false,
		readingAidsChangeboardingStep: 'disabled',
	});
	const [position, setPosition] = useState<Position | null>(null);

	const { hasContentChanged } = useCommentsContentState();
	const { createComment } = useInlineCommentsDispatchContext();
	const { commentToReattach, isInReattachMode } = useReattachComment();
	const { setReattachHighlight } = useReattachCommentDispatch();
	const { lastModificationDate } = useContext(ContentBodyContext);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { canAddComments, contentQueryError } = useAddCommentPermissionCheck(
		contentId,
		false,
		preloadedCanAddComments,
	);
	const [{ searchSessionId, additionalAnalytics }] = useSearchSessionId();
	const { documentNeedsUpdate, currentDocumentVersion, publishedDocumentVersion } =
		useDocumentUpdateStatus();

	const commentShortcutStrings = useShortcutStrings({ shortcut: UNIVERSAL_OPEN_COMMENT_EDITOR });

	const rightSidebarContext = useRightSidebarContext();
	const [isWarningDialogOpen, setIsWarningDialogOpen] = useState<boolean>(false);
	const [recordedClick, setRecordedClick] = useState<CreateIssueRecordedProps>();
	const shouldDisableCommentWhenDocumentNeedsUpdate =
		inlineNodeTypes?.length === 1 &&
		['status', 'date', 'mention', 'inlineCard'].includes(inlineNodeTypes[0]);
	const needsUpdateToComment =
		shouldDisableCommentWhenDocumentNeedsUpdate &&
		(Boolean(documentNeedsUpdate) ||
			(currentDocumentVersion !== publishedDocumentVersion &&
				expValEquals('confluence_fe_disable_comment_if_page_outdated_v2', 'isEnabled', true)));
	// Flag to trigger text comment creation after media comment cleanup
	const [pendingTextComment, setPendingTextComment] = useState(false);

	const { setDefineDefinitionOpen } = useReadingAidsActions();
	const { hoverProps } = useAnnotations();
	const { setHoverProps } = useAnnotationsDispatch();

	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	const {
		isRemixing: isRemixingFromContext,
		isRemixInProgress,
		isRemixBlockingNewTargets,
		isAnyRemixing,
	} = useRendererRemixContext();
	const isRemixing = isRemixingFromContext;
	// Variant 1 retains its local picker control, but no local exception can replace a target
	// while another treatment surface owns an admitted Remix flow.
	const shouldDisableRemix = UNSAFE_expValEqualsNoExposure(
		'cc-ce-maui-remix-menu-multivariant',
		'cohort',
		'variant1',
	)
		? isRemixInProgress || isAnyRemixing
		: isRemixBlockingNewTargets;

	const {
		firstHighlightShown,
		show,
		top,
		left,
		forgeExtension,
		activeSelection,
		selectionRect,
		isReadingAidsOpen,
		readingAidsChangeboardingStep,
	} = actionStates;

	const mountedRef = useRef(false);
	const screenEventFiredRef = useRef(false);
	const highlightActionsShownRef = useRef(false);

	const content = useRef<HTMLDivElement | null>(
		typeof document !== 'undefined' ? document.querySelector('div#content') : null,
	);

	const [showReadingAidsChangeboarding, stopReadingAidsChangeboarding] = useFeatureDiscovery(
		'highlight-actions.reading-aids.changeboarding',
	);

	const isReadingAidsChangeboardingActive = readingAidsChangeboardingStep.match(/^modal|spotlight/);

	// Sync popup visibility to OmniBar's visibility store (counter-based)
	const { requestHideOmniBar, releaseHideOmniBar } = useOmniBarVisibilityActions();
	const shouldHideOmniBar = show || isReadingAidsOpen || !!forgeExtension;
	useLayoutEffect(() => {
		if (shouldHideOmniBar) {
			requestHideOmniBar();
			return () => releaseHideOmniBar();
		}
	}, [shouldHideOmniBar, requestHideOmniBar, releaseHideOmniBar]);

	const highlightActionsContextValue = useMemo(
		() => ({ firstHighlightShown }),
		[firstHighlightShown],
	);

	// start the reading aids changeboarding
	useEffect(() => {
		setActionStates((prevState) => ({
			...prevState,
			readingAidsChangeboardingStep:
				showReadingAidsChangeboarding && isAIEnabledForContent ? 'idle' : 'disabled',
		}));
	}, [showReadingAidsChangeboarding, isAIEnabledForContent]);

	useEffect(() => {
		// Manually trigger resize to ensure spotlight is properly aligned
		window.setTimeout(() => window.dispatchEvent(new Event('resize')), 0);

		if (!show) {
			// Force a redraw of portalContainer, which fixes an issue where the popup is removed from DOM but is
			// still visible on the page __in Safari__. (https://product-fabric.atlassian.net/browse/COMMENTS-5335)
			// This rendering issue occurring in Safari's WebKit engine seems to be known and has a long history.
			// Only do this when `show === false`, since the issue only occurs when attepmting to hide the popup.
			const portalContainer = document.querySelector<HTMLDivElement>(`#${popupPortalContainerId}`);
			if (portalContainer) {
				portalContainer.style.transform = 'translateZ(0)';
				// This line below reads this layout property, which triggers a reflow + redraw as side effect.
				portalContainer.offsetHeight; // NOSONAR - Sonar would complain: "Expected an assignment or function call and instead saw an expression"
				portalContainer.style.transform = '';
			}
		}
	}, [show]);

	// freeze the user's selection while reading aids changeboarding is active
	useEffect(() => {
		const akPortalContainer = document.querySelector<HTMLDivElement>('.atlaskit-portal-container');
		if (akPortalContainer) {
			if (isReadingAidsChangeboardingActive) {
				akPortalContainer.style.userSelect = 'none';
				return () => {
					akPortalContainer.style.userSelect = 'auto';
				};
			} else {
				akPortalContainer.style.userSelect = 'auto';
			}
		}
	}, [isReadingAidsChangeboardingActive]);

	useLayoutEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		document.addEventListener('keydown', trapFocusInSelectionToolbar);

		return () => {
			document.removeEventListener('keydown', trapFocusInSelectionToolbar);
		};
	}, []);

	useEffect(() => {
		window.addEventListener('mouseup', onMouseUp);
		window.addEventListener('highlight-actions.container.resized', onContainerResized);

		return () => {
			window.removeEventListener('mouseup', onMouseUp);
			window.removeEventListener('highlight-actions.container.resized', onContainerResized);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, commentToReattach, isReadingAidsChangeboardingActive, isRemixing]);

	/* When nudgebar improvement is on: hide toolbar when selection was set by comment nudge (e.g. on hover). */
	useEffect(() => {
		if (!expValEquals('confluence_comment_nudgebar_improvement', 'isEnabled', true)) return;
		const onSelectionChange = () => {
			if (
				document.body?.getAttribute?.('data-nudge-selection-active') === 'true' &&
				mountedRef.current
			) {
				setActionStates((prev) => ({ ...prev, show: false }));
			}
		};
		document.addEventListener('selectionchange', onSelectionChange);
		return () => document.removeEventListener('selectionchange', onSelectionChange);
	}, []);

	// Close reading aids popup when navigating to another page
	useEffect(() => {
		return () => {
			void setDefineDefinitionOpen({ defineDefinitionOpen: false });

			setActionStates((prevState) => ({
				...prevState,
				isReadingAidsOpen: false,
			}));
		};
	}, [contentId, setDefineDefinitionOpen]);

	// Handle text comment creation after media comment cleanup is complete
	useEffect(() => {
		if (pendingTextComment && !hoverProps) {
			setPendingTextComment(false);
			if (isFabric) {
				onCommentButtonPress?.();
			} else {
				createComment(
					getLegacyConvertedSelectionObj({
						content,
						contentId,
						commentToReattach,
						actions,
					}),
				);
			}
		}
	}, [
		pendingTextComment,
		hoverProps,
		isFabric,
		onCommentButtonPress,
		createComment,
		content,
		contentId,
		commentToReattach,
		actions,
	]);

	const onContainerResized = () => {
		setActionStates((prevState) => ({
			...prevState,
			show: false,
			forgeExtension: null,
		}));
	};

	function getScrollParent(node: any): any {
		if (!node) {
			return null;
		}

		if (node.nodeType !== Node.TEXT_NODE && node.scrollWidth > node.clientWidth) {
			return node;
		} else {
			return getScrollParent(node.parentNode);
		}
	}

	function getParentNode(node: any): HTMLElement | null {
		if (!node) {
			return null;
		}

		if (node.nodeType !== Node.TEXT_NODE) {
			return node;
		} else {
			return getParentNode(node.parentNode);
		}
	}

	const getPosition = (range: Range) => {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const portalContainer = document.querySelector(`#${popupPortalContainerId}`);
		const portalRect = portalContainer?.getBoundingClientRect() || {
			left: 0,
			top: 0,
		};

		const { left: leftRect, top: topRect, width, height } = range.getBoundingClientRect();

		// Popup can't be rendered outside the body area
		const contentRect = getParentNode(range.commonAncestorContainer)!.getBoundingClientRect();
		let leftLimit = contentRect.left;
		let rightLimit = contentRect.right;

		// If the selected text has a parent that is scrollable for example like text inside table.
		// The popup is limited to be shown within the scrollable area.
		const firstParentThatCanScroll = getScrollParent(range.startContainer);
		if (firstParentThatCanScroll) {
			const scrollParentRect = firstParentThatCanScroll.getBoundingClientRect();
			leftLimit = Math.max(leftLimit, scrollParentRect.left);
			rightLimit = Math.min(rightLimit, scrollParentRect.right);
		}

		let x = width / 2 + leftRect;
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (!fg('kd_definitions_skip_displacement_computation')) {
			x = Math.max(leftLimit, Math.min(rightLimit, x));
		}

		return {
			pos: {
				left: x - portalRect.left,
				top: topRect - portalRect.top,
			},
			selectionRect: {
				left: x - portalRect.left,
				top: topRect - portalRect.top,
				width,
				height,
			},
		};
	};

	useEffect(() => {
		if (range) {
			/* When nudgebar improvement is on, never show toolbar for selection set by nudge (range can come from parent when we set selection on comment hover). */
			if (
				document.body?.getAttribute?.('data-nudge-selection-active') === 'true' &&
				expValEquals('confluence_comment_nudgebar_improvement', 'isEnabled', true)
			) {
				setPosition(null);
				setActionStates((prevState) => ({ ...prevState, show: false }));
				return;
			}
			const { pos, selectionRect: selectionRectangle } = getPosition(range);
			highlightActionsShownRef.current = false;
			setPosition(pos);
			setActionStates((prevState) => ({
				...prevState,
				selectionRect: selectionRectangle,
				show: true,
			}));
		} else {
			setPosition(null);
			setActionStates((prevState) => ({
				...prevState,
				show: false,
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [range]);

	const updatePosition = useCallback(() => {
		if (range) {
			const { pos } = getPosition(range);
			setPosition(pos);
			return pos;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [range]);

	const prepareAndSetHighlightForReattachMode = () => {
		const convertedSelectionObj = getLegacyConvertedSelectionObj({
			content,
			contentId,
			commentToReattach,
			actions,
		});

		// Only set the highlight if it's valid
		if (convertedSelectionObj.numMatches > 0 && convertedSelectionObj.matchIndex !== -1) {
			setReattachHighlight(convertedSelectionObj);
		}
	};

	const isDatasourceHighlighted = (range: Range) => {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const datasourceContainers = document.querySelectorAll('.datasource-table');

		return Array.from(datasourceContainers).some(
			(container) =>
				container.contains(range.startContainer) || container.contains(range.endContainer),
		);
	};

	const onMouseUp = (e) => {
		return requestAnimationFrame(() => {
			if (!mountedRef.current) {
				return;
			}

			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (fg('quick_capture_trello_inbox')) {
				void prefetchTrelloAccess();
			}

			const range = getRange();
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			const portalContainer = document.querySelector(`#${popupPortalContainerId}`);

			// WS-1576/WS-2355 - Ignore any highlights in the Inline Comments sidebar
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			const inlineCommentSidebarContainer = document.querySelector('#inline-comment-sidebar');

			// InlineCommentNudge handles its own content selection and opening of the Comment Editor.
			// Check both event target and element under cursor so we don't show toolbar when pointer is over nudge (e.g. portal/overlay edge cases).
			const elementUnderCursor =
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				typeof document.elementFromPoint === 'function'
					? // eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
						document.elementFromPoint(e.clientX, e.clientY)
					: null;
			const isOverCommentNudge =
				(e.target && (e.target as Element).closest?.('#inline-comment-nudge')) ||
				(elementUnderCursor && elementUnderCursor.closest?.('#inline-comment-nudge'));
			const isOverRemixNudge =
				(e.target && (e.target as Element).closest?.('#inline-remix-nudge')) ||
				(elementUnderCursor && elementUnderCursor.closest?.('#inline-remix-nudge'));

			if (isOverCommentNudge) {
				return;
			}
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (isOverRemixNudge && fg('confluence_remix_button_right_side_block_fg')) {
				return;
			}
			// When nudgebar improvement is on, only show toolbar when user selected text (not when selection was set by nudge hover).
			if (
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				document.body?.getAttribute?.('data-nudge-selection-active') === 'true' &&
				expValEquals('confluence_comment_nudgebar_improvement', 'isEnabled', true)
			) {
				return;
			}

			// freeze the actions popup if reading aids changeboarding is active
			if (isReadingAidsChangeboardingActive) {
				return;
			}

			// freeze the actions popup if the input is clicked in the rovo chat more options popup
			if (e.target.closest('#context-menu-rovo-chat-custom-prompt')) {
				return;
			}

			const isCommentButtonDisabledAndClicked = e.target.closest('button[disabled]');
			const isContextMenuOtherAppsButtonClicked = e.target.closest('#context-menu-other-apps');
			const isCreateJiraIssueButtonClicked = e.target.closest('#create-jira-issue-trigger');
			const isRovoChatMoreOptionsButtonClicked = e.target.closest(
				'#context-menu-rovo-chat-more-options',
			);
			const isNotesButtonClicked = e.target.closest('#highlight-actions-notes-button');

			// The anchored menu opens in place, so clicking its trigger must not dismiss the
			// toolbar the menu is anchored to. Clicking it can also collapse the browser
			// selection before getRange() is read, which would hit the generic `!range`
			// dismissal path below.
			if (
				e.target.closest(HIGHLIGHT_ACTIONS_REMIX_TRIGGER_SELECTOR) &&
				UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant1')
			) {
				return;
			}

			const shouldHideContextMenu = !(
				isCommentButtonDisabledAndClicked ||
				isContextMenuOtherAppsButtonClicked ||
				isCreateJiraIssueButtonClicked ||
				isRovoChatMoreOptionsButtonClicked ||
				isNotesButtonClicked
			);
			if (
				!range ||
				!portalContainer ||
				!content.current ||
				inlineCommentSidebarContainer?.contains(range.startContainer) ||
				inlineCommentSidebarContainer?.contains(range.endContainer) ||
				isDatasourceHighlighted(range) ||
				(!content.current.contains(e.target) &&
					content.current !== e.target &&
					shouldHideContextMenu)
			) {
				if (isInReattachMode()) {
					setReattachHighlight(null);
				}
				setActionStates((prevState) => ({ ...prevState, show: false }));
				return;
			}

			// COMMENTS-332 - In reattach mode, skip the measuring of the screen, just set the mode with the highlight
			if (isInReattachMode()) {
				prepareAndSetHighlightForReattachMode();
				setActionStates((prevState) => ({ ...prevState, show: false }));
				return;
			}

			const portalRect = portalContainer.getBoundingClientRect();
			const contentRect = content.current.getBoundingClientRect();
			const rects = range.getClientRects();
			if (!rects || !rects.length) {
				setActionStates((prevState) => ({ ...prevState, show: false }));
				return;
			}

			// Find first selection area that width is not 0
			// Sometimes there is a chance that user is selecting an empty DOM node.
			const rect = Array.from(rects).find((rect) => rect.width !== 0 && rect.height !== 0);

			if (rect) {
				const { left, right, top, width, height } = rect;
				const mouseUpX = e.clientX;
				// This is a LEGACY disable statement after rule addition. Use `Math.floor(width/2)` next time
				// eslint-disable-next-line no-bitwise
				const selectedLineCenter = left + (width >> 1);

				// Popup can't be rendered outside the body area
				let leftLimit = contentRect.left;
				let rightLimit = contentRect.right;

				// If the selected text has a parent that is scrollable for example like text inside table.
				// The popup is limited to be shown within the scrollable area.
				const firstParentThatCanScroll = getScrollParent(range.startContainer);
				if (firstParentThatCanScroll) {
					const scrollParentRect = firstParentThatCanScroll.getBoundingClientRect();
					leftLimit = Math.max(leftLimit, scrollParentRect.left);
					rightLimit = Math.min(rightLimit, scrollParentRect.right);
				}

				// Use the X-axis position if the center of selected line is POPUP_MOUSEUP_DISTANCE_THRESHOLD away from mouse up position.
				// POPUP_MOUSEUP_DISTANCE_THRESHOLD is an arbitrary number I made up.
				// The idea is the popup will always show up within POPUP_MOUSEUP_DISTANCE_THRESHOLD of the end of the selection.
				// Right near what user selects. However when the selection is too short - like a phase,
				// it will show up at the middle of the selection because it looks better.
				let x =
					Math.abs(mouseUpX - selectedLineCenter) > POPUP_MOUSEUP_DISTANCE_THRESHOLD &&
					left <= mouseUpX &&
					mouseUpX <= right
						? mouseUpX
						: selectedLineCenter;

				// However it is still bounded by the left and right boundary of the first line.
				// This is because the mouse up can be off screen.
				// For example: Make selection, then drag on a scroll bar.
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				if (!fg('kd_definitions_skip_displacement_computation')) {
					x = Math.max(leftLimit, Math.min(rightLimit, x));
				}

				if (!isFabric) {
					highlightActionsShownRef.current = false;
				}

				setActionStates((prevState) => ({
					...prevState,
					firstHighlightShown: true,
					// COMMENTS-332 - Don't show the popup when a selection is made in reattach mode
					show: !isInReattachMode(),
					left: x - portalRect.left,
					top: top - portalRect.top,
					selectionRect: {
						left: x - portalRect.left,
						top: top - portalRect.top,
						width,
						height,
					},
				}));
			}
		});
	};

	const createReactInlineCommentHighlight = () => {
		if (isObjectSidebarShown) {
			void hideObjectSidebar();
		}

		// Close any existing media create comment when opening a text comment
		if (expValEquals('confluence_comment_unmount_onmedia_selection', 'isEnabled', true)) {
			if (hoverProps) {
				// Remove draft annotation from media element if it exists
				hoverProps.removeDraftMode?.();
				// Close the hover component (calls renderer's onClose)
				hoverProps.onClose?.();
				// Clear hoverProps from context
				setHoverProps(null);
				// Flag to trigger text comment creation after hoverProps is cleared
				setPendingTextComment(true);
				return;
			}
		}

		// Proceed with text comment creation immediately if no media comment to clean up
		if (isFabric) {
			onCommentButtonPress?.();
		} else {
			createComment(
				getLegacyConvertedSelectionObj({
					content,
					contentId,
					commentToReattach,
					actions,
				}),
			);
		}
	};

	const handleCommentButtonClick = (dialogs) => {
		if (hasContentChanged) {
			dialogs.showDialog(CommentWarningDialog, {
				onConfirm: () => createReactInlineCommentHighlight(),
			});
		} else {
			createReactInlineCommentHighlight();
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'inlineCommentFromInlineToolbar',
				action: 'clicked',
				actionSubject: 'button',
				type: 'ui',
				attributes: {
					contentId,
					pageMode: 'view',
					searchSessionId,
					...additionalAnalytics,
				},
			},
		}).fire();
	};

	const extractNodeDetailsToString = (node?: Node | null) => {
		if (!node) {
			return '';
		}

		while (node?.nodeType === Node.TEXT_NODE || (node as HTMLElement).tagName === 'MARK') {
			node = node?.parentNode;
		}

		if (node) {
			const elem = node as HTMLElement;
			const classListStr = Array.from(elem.classList).join(',');

			let attrListStr = '';
			Object.entries(elem.dataset).forEach((attrEntry, idx) => {
				if (idx > 0) {
					attrListStr += ',';
				}

				// COMMENTS-1916 - Remove any emails that are in any values for attributes
				if (attrEntry.length > 1) {
					const field = attrEntry[0];
					if (field && field.includes('testMediaName')) {
						attrEntry[1] = '';
					}
				}

				attrListStr += attrEntry.join('=');
			});

			// Get the tag name and stringify the class list and attribute list
			return `${elem.tagName}${classListStr.length ? `.${classListStr}` : ''}${
				attrListStr.length ? `[${attrListStr}]` : ''
			}`;
		}

		return '';
	};

	const CreateInlineCommentButton = () => {
		useEffect(() => {
			onCommentButtonMount && onCommentButtonMount();
		}, []);

		if (!content.current) {
			return null;
		}

		const isHighlightValid = getIsHighlightValid({
			content: content.current,
			isFabric,
			isAnnotationAllowed,
			contentId,
		});

		// Cases where we want to disable the create button:
		// 1. The content is archived.
		// 2. The user does not have the "Comment - Add" permission.
		// 3. The highlight was invalid (over elements we don't support commenting on).
		// 4. The document needs to be updated (i.e. is outdated) AND selected node is one of [status, date, mention, inlineCard].
		// This order matters when communicating to users, as checking "Comment - Add" permission for archived pages
		// will return false.
		const isCreateButtonEnabled =
			!isArchived && canAddComments && !needsUpdateToComment && isHighlightValid;

		// Check if the comment is on a selection that includes an non-text inline node
		const isNonTextInlineNodeIncludedInComment =
			(inlineNodeTypes ?? []).filter((nodeType) => nodeType !== 'text').length > 0;

		// Track the nodes in which the highlight was made on to send in the event
		if (!highlightActionsShownRef.current) {
			highlightActionsShownRef.current = true;
			try {
				const selection = window.getSelection();
				const startingNode = extractNodeDetailsToString(selection?.anchorNode);
				const endingNode = extractNodeDetailsToString(selection?.focusNode);

				// Track when the menu is shown to users and if it's disabled
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'highlightActionsMenu',
						action: 'shown',
						actionSubject: 'toolbar',
						actionSubjectId: 'highlightActionsMenu',
						attributes: {
							isDisabled: !isCreateButtonEnabled,
							pageMode: 'view',
							startingNode,
							endingNode,
							inlineNodeTypes,
							/**
							 * This attribute is used as the trigger to display an engagement platform promotion message
							 * when isNonTextInlineNodeIncludedInComment is true, and isDisabled is false,
							 * A spotlight/flag will be shown to the user to encourage them to comment on inline nodes.
							 */
							isNonTextInlineNodeIncludedInComment,
						},
					},
				}).fire();
			} catch (e) {
				logger.error`Unable to send highlight actions event; ${e}`;
			}
		}

		const createButton = (
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs: DialogsStateContainer) => {
					const commonCreateButtonProps = {
						'data-key':
							'com.atlassian.confluence.plugins.confluence-inline-comments:create-inline-comment', // Needed for integration tests
						isDisabled: !isCreateButtonEnabled,
						testId: 'createInlineCommentButton',
						interactionName: 'start-inline-comment-action',
						onClick: () => {
							handleCommentButtonClick(dialogs);
						},
						id: 'create-inline-comment-button',
					};

					return (
						<ToolbarButtonGroup>
							<ToolbarButton
								{...commonCreateButtonProps}
								iconBefore={<CommentIcon label="" size="small" />}
							>
								<FormattedMessage
									id="highlight-actions.inline-comments.comment"
									defaultMessage="Comment"
									description="Make a new comment"
								/>
							</ToolbarButton>
						</ToolbarButtonGroup>
					);
				}}
			</Subscribe>
		);

		// WS-2802 - We need to wrap the disabled button and put a transparent absolute overlay over the button
		//           in order to show the tooltip correctly as the disabled button swallows all events on the element
		//           and ADG doesn't allow tooltips on disabled buttons for accessibility reasons...
		return isCreateButtonEnabled ? (
			<Fragment key="inline-comment-button">
				<ToolbarTooltip
					content={formatMessage(i18n.commentTooltip)}
					shortcut={commentShortcutStrings}
				>
					{createButton}
				</ToolbarTooltip>
			</Fragment>
		) : (
			<ToolbarTooltip
				delay={0}
				content={getCreateButtonDisabledMessage(
					isArchived,
					canAddComments,
					needsUpdateToComment,
					contentType,
				)}
			>
				<DisabledButtonWrapper key="inline-comment-button">
					<DisabledButtonOverlay />
					{createButton}
				</DisabledButtonWrapper>
			</ToolbarTooltip>
		);
	};

	const createJiraIssueButton = (completeKey: string) => {
		if (!content.current || isEmbeddedConfluence_DO_NOT_USE() || isMobileApp_DO_NOT_USE()) {
			return null;
		}

		if (!StatsigConfigurations.getBoolean('confluence_legacy_single_jira_work_create', false)) {
			return (
				<SingleIssueCreateProvidersWrapper>
					<AIEventsInstrumentationProvider
						config={{
							// Required configuration
							product: 'confluence',
							subproduct: 'jira',
							aiFeatureName: 'confluenceAiWorkCreation',
							proactiveGeneratedAI: 0,
							userGeneratedAI: 1,
							// Optional configuration
							source: 'highlightActionsMenu',
							skipAISessionValidation: true,
						}}
					>
						<CreateJiraIssueActions
							activeSelection={window.getSelection()}
							completeKey={completeKey}
							container={content.current}
							contentId={contentId}
							contentType={contentType}
							lastModified={lastModifiedDate || lastModificationDate}
							showCreateMultipleIssuesButton={isHighlightValidForBulkCreateJiraIssues()}
							setRecordedClick={setRecordedClick}
							setIsWarningDialogOpen={setIsWarningDialogOpen}
						/>
					</AIEventsInstrumentationProvider>
				</SingleIssueCreateProvidersWrapper>
			);
		}

		return (
			<NewButton
				appearance="subtle"
				iconBefore={() => <AddIssue label="" />}
				data-key={completeKey}
				testId="createJiraIssueButton"
				onClick={createJiraIssueSummaryHandle(
					content.current,
					contentId,
					lastModifiedDate || lastModificationDate,
				)}
			>
				{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
				{fg('confluence-issue-terminology-refresh') ? (
					<FormattedMessage
						id="highlight-actions.create-issue-issue-term-refresh"
						defaultMessage="Create work item"
						description="Create a new Jira issue"
					/>
				) : (
					<FormattedMessage
						id="highlight-actions.create-issue"
						defaultMessage="Create issue"
						description="Create a new Jira issue"
					/>
				)}
			</NewButton>
		);
	};

	const createTrelloInboxButton = (completeKey: string) => {
		if (!content.current || isEmbeddedConfluence_DO_NOT_USE() || isMobileApp_DO_NOT_USE()) {
			return null;
		}

		return (
			<AddToTrelloInboxActions
				activeSelection={window.getSelection()}
				completeKey={completeKey}
				contentId={contentId}
			/>
		);
	};

	const createReadingAidsDefineButton = ({
		selectedRange,
		selectedText,
		isOverWordLimit,
	}: {
		selectedRange: Range | null;
		selectedText: string;
		isOverWordLimit: boolean;
	}) => {
		if (!content.current) {
			return null;
		}

		const isDisabled =
			(isOverWordLimit && readingAidsChangeboardingStep === 'disabled') || // only check word limit when changeboarding is disabled
			readingAidsChangeboardingStep === 'spotlight'; // always disable when spotlight is active

		const commonDefineButtonProps = {
			testId: 'defineButton',
			isDisabled,
			onClick: () => {
				if (!selectedRange) {
					return;
				}

				if (!selectedText) {
					return;
				}

				if (isAIEnabledForContent || isRovoEnabledForContent) {
					experienceTracker.start({
						name: READING_AIDS_EXPERIENCE,
						attributes: {
							contentId,
							source: 'defineButton',
						},
					});
				}

				void setDefineDefinitionOpen({ defineDefinitionOpen: true });

				setActionStates((prevState) => ({
					...prevState,
					...(isOverWordLimit && readingAidsChangeboardingStep === 'idle'
						? { readingAidsChangeboardingStep: 'modal-word-count' }
						: { isReadingAidsOpen: true }),
					show: false,
					activeSelection: {
						selectedText,
						selectedRange,
					},
				}));

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'highlightActionsMenu',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'defineButton',
						type: 'ui',
						attributes: {
							contentId,
							isAIOptedIn: isAIEnabledForContent,
						},
					},
				}).fire();
			},
		};

		const defineButtonContent = (
			<ToolbarTooltip content={formatMessage(i18n.defineTooltip)}>
				<ToolbarButtonGroup>
					<ToolbarButton
						{...commonDefineButtonProps}
						iconBefore={<DefineIcon label="" size="small" />}
					>
						<FormattedMessage
							id="highlight-actions.define"
							defaultMessage="Define"
							description="Tooltip for button that opens the reading aids popup, which provides an AI-generated definition for the highlighted term"
						/>
					</ToolbarButton>
				</ToolbarButtonGroup>
			</ToolbarTooltip>
		);

		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		const defineButton = fg('confluence_frontend_spotlight_ads_migration') ? (
			<PopoverTarget>{defineButtonContent}</PopoverTarget>
		) : (
			<SpotlightTarget name={READING_AIDS_CHANGEBOARDING_SPOTLIGHT_ID}>
				{defineButtonContent}
			</SpotlightTarget>
		);

		return isDisabled ? (
			<Tooltip
				delay={0}
				position="top"
				content={
					<FormattedMessage
						id="highlight-actions.define.unsupported"
						description="Tooltip for the unsupported reading aids search on content that is over the word count limit"
						defaultMessage="Highlight a term, acronym, or abbreviation"
					/>
				}
				component={
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					(fg('confluence_comment_border_migration')
						? CustomLineWrapTooltipUpdated
						: CustomLineWrapTooltip) as ComponentType<TooltipPrimitiveProps>
				}
			>
				<DisabledButtonWrapper>
					<DisabledButtonOverlay />
					{defineButton}
				</DisabledButtonWrapper>
			</Tooltip>
		) : (
			defineButton
		);
	};

	const handleClickChat = useCallback(() => {
		const { panelId } = rightSidebarContext.getCurrent() || {};
		const isChatActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID;
		if (!isChatActive) {
			rightSidebarContext.open(
				CONVERSATION_ASSISTANT_CONTAINER_ID,
				CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
			);
		}
	}, [rightSidebarContext]);

	const createRovoChatHighlightActionsButton = (selectedADFContent: DocNode) => {
		if (isEmbeddedConfluence_DO_NOT_USE() || isMobileApp_DO_NOT_USE()) {
			return null;
		}

		return (
			<RovoChatHighlightActions
				selectedContent={selectedADFContent}
				product="confluence"
				onClickChat={handleClickChat}
			/>
		);
	};

	const renderBuiltInWebItemActions = (builtInWebItems: Array<{ completeKey: string }>) => {
		const isHighlightValid = getIsHighlightValid({
			content: content.current,
			isFabric,
			isAnnotationAllowed,
			contentId,
		});
		const isReactionAllowed =
			!isArchived && canAddComments && !needsUpdateToComment && isHighlightValid;
		const componentsAI: React.JSX.Element[] = [];
		const components: React.JSX.Element[] = [];
		const showMoreActions: React.JSX.Element[] = [];

		const selectedRange = getRange();
		const selectedText = textNormalize(String(window.getSelection())).replace(/\n/, ' ').trim();
		const wordCount = selectedText.split(/\s+/).length;
		const isOverWordLimit = wordCount > READING_AIDS_MAX_WORD_COUNT;
		const showReadingAidsDefineButton = isAIEnabledForContent || isRovoEnabledForContent;
		const selectionString = getHTMLString(window.getSelection());
		const selectedADFContent = htmlToADF(selectionString);

		if (isRovoEnabledForContent) {
			const askRovoButton = createRovoChatHighlightActionsButton(selectedADFContent);
			if (askRovoButton) {
				componentsAI.push(askRovoButton);
			}
		}
		if (showReadingAidsDefineButton && !isOverWordLimit) {
			const defineButton = createReadingAidsDefineButton({
				selectedRange,
				selectedText,
				isOverWordLimit,
			});
			if (defineButton) {
				componentsAI.push(defineButton);
			}
		}

		if (isRemixAvailable && !isEmbeddedConfluence_DO_NOT_USE()) {
			const remixButton = (
				<RemixAction
					key="RemixButton"
					onClick={onRemixButtonPress}
					onMouseDown={onRemixButtonMouseDown}
					isDisabled={!onRemixButtonPress}
					isRemixInProgress={shouldDisableRemix}
					remixDisabledReason={remixDisabledReason}
				/>
			);
			componentsAI.push(remixButton);
		}

		const commentButton = <CreateInlineCommentButton key="CreateInlineCommentButton" />;
		components.push(commentButton);

		if (expValEquals('confluence_inline_reactions', 'isEnabled', true)) {
			components.push(
				<InlineReactionAction
					key="InlineReactionAction"
					isAnnotationAllowed={isReactionAllowed}
					disabledTooltip={
						!isReactionAllowed
							? getReactionButtonDisabledMessage(
									isArchived,
									canAddComments,
									needsUpdateToComment,
									contentType,
								)
							: undefined
					}
				/>,
			);
		}

		let jiraButton: React.JSX.Element | null = null;
		let trelloButton: React.JSX.Element | null = null;

		builtInWebItems.forEach((item) => {
			const { completeKey } = item;
			if (
				completeKey ===
				'com.atlassian.confluence.plugins.confluence-jira-content:create-JIRA-issue-summary'
			) {
				jiraButton = createJiraIssueButton(completeKey);
			} else if (
				completeKey ===
				'com.atlassian.confluence.plugins.confluence-trello-content:create-trello-card-quick-capture'
			) {
				trelloButton = createTrelloInboxButton(completeKey);
			}
		});

		if (
			snippetSlots?.dropdownItem &&
			expValEquals('platform_editor_blocks_conf', 'isEnabled', true)
		) {
			showMoreActions.push(
				<Fragment key="SnippetDropdownItem">{snippetSlots.dropdownItem}</Fragment>,
			);
		}
		if (jiraButton) {
			showMoreActions.push(jiraButton);
		}
		if (trelloButton) {
			showMoreActions.push(trelloButton);
		}

		if (componentsAI.length > 0) {
			// if there are AI components, render them in a ToolbarSection with a separator at the end
			return {
				builtInActions: (
					<>
						<ToolbarSection key="built-in-actions-ai" hasSeparator={SeparatorPosition.END}>
							{componentsAI}
						</ToolbarSection>
						<ToolbarSection key="built-in-actions">{components}</ToolbarSection>
					</>
				),
				showMoreActions,
			};
		}
		return {
			builtInActions: <ToolbarSection key="built-in-actions">{components}</ToolbarSection>,
			showMoreActions,
		};
	};

	const getComponentsForPopup = (
		webItems,
		forgeItems: ReadonlyArray<ForgeUIExtensionType> = [],
	) => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		const webItemsWithTrello = fg('quick_capture_trello_inbox')
			? [
					...webItems,
					{
						completeKey:
							'com.atlassian.confluence.plugins.confluence-trello-content:create-trello-card-quick-capture',
					},
				]
			: webItems;

		const builtInWebItems = webItemsWithTrello.filter((item) =>
			BUILT_IN_ITEMS.includes(item.completeKey),
		);
		const connectItems = webItems.filter(
			(item) => !DROPDOWN_EXCLUDED_ITEMS.includes(item.completeKey),
		);
		const { builtInActions, showMoreActions } = renderBuiltInWebItemActions(builtInWebItems);

		const otherActions = (
			<ToolbarSection>
				<ToolbarButtonGroup key="other-components">
					<OtherActions
						builtInActions={showMoreActions}
						connectItems={connectItems}
						forgeItems={forgeItems}
						selectForgeExtension={(forgeExtension) => {
							const selectedRange = getRange();
							if (!selectedRange) {
								return;
							}
							const selectedText = textNormalize(String(selectedRange)).trim();
							if (!selectedText) {
								return;
							}

							setActionStates((prevState) => ({
								...prevState,
								forgeExtension,
								activeSelection: {
									selectedText,
									selectedRange,
								},
							}));
						}}
					/>
				</ToolbarButtonGroup>
			</ToolbarSection>
		);

		return [builtInActions, otherActions];
	};

	const renderPopup = (components) =>
		components.length ? (
			<Subscribe to={[BannerStateContainer]}>
				{(bannerState: BannerStateContainer) => {
					// if there's a sticky header we want to position the toolbar below
					// the nav, the page header (if visible) and the sticky header
					let stickyHeaderHeight = 0;
					const range = getRange();
					if (range) {
						const header = findStickyHeader(range.commonAncestorContainer);
						if (header && bannerState) {
							stickyHeaderHeight = bannerState.getTotalHeight() + header.clientHeight;
						}
					}

					if (!screenEventFiredRef.current) {
						createAnalyticsEvent({
							type: 'sendScreenEvent',
							data: {
								name: 'highlightActionsMenu',
								attributes: {
									contentId,
								},
							},
						}).fire();
					}
					screenEventFiredRef.current = true;

					return (
						<Fragment>
							<Popup
								top={stickyHeaderHeight + (position?.top ?? top)}
								left={position?.left ?? left}
								updatePosition={updatePosition}
							>
								{components}
							</Popup>

							<ExperienceSuccess name={VIEW_PAGE_CONTEXT_MENU_EXPERIENCE} />
						</Fragment>
					);
				}}
			</Subscribe>
		) : null;

	const isCreateSingleJiraIssueHandleParams = (
		params: CreateIssueRecordedProps,
	): params is CreateSingleJiraIssueHandleParams => {
		return (params as CreateSingleAiJiraIssueHandleParams)?.fireAiAnalytics === undefined;
	};

	const handleConfirm = useCallback(async () => {
		const callRightSidebarClose = async () => rightSidebarContext.close(ISSUE_CREATE_SIDE_PANEL_ID);

		// Need to make sure sidebar is closed before creating a new one issue
		await callRightSidebarClose();

		// Sorry this block sucks because of TS types
		if (isCreateSingleJiraIssueHandleParams(recordedClick)) {
			createSingleJiraIssueHandle(recordedClick);
		} else {
			recordedClick && createSingleAiJiraIssueHandle(recordedClick);
		}

		setIsWarningDialogOpen(false);
	}, [setIsWarningDialogOpen, recordedClick, rightSidebarContext]);

	const handleCancel = useCallback(() => {
		setIsWarningDialogOpen(false);
	}, [setIsWarningDialogOpen]);

	const contentSection = !isFabric && (
		<HighlightActionsContext.Provider value={highlightActionsContextValue}>
			<div key="content-container" ref={content}>
				{children}
			</div>
		</HighlightActionsContext.Provider>
	);

	const popupSection = (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			{show ? (
				<Fragment>
					<ExperienceStart id={contentId} name={VIEW_PAGE_CONTEXT_MENU_EXPERIENCE} />
					<WebItemLocation
						key="action-panel"
						contentId={contentId}
						location={ACTION_PANEL}
						fetchPolicy="cache-first"
					>
						{({ webItems }) => (
							<LazyForgeUIExtensions
								moduleType={FORGE_MODULE_CONTEXT_MENU}
								render={(forgeItems, loading, error) => {
									if (loading) {
										return null;
									}
									if (error) {
										if (!isErrorMarkedAsHandled(error)) {
											return <ErrorDisplay error={error} />;
										}
										return null;
									}

									return renderPopup(getComponentsForPopup(webItems, forgeItems));
								}}
							/>
						)}
					</WebItemLocation>
				</Fragment>
			) : null}
		</ErrorBoundary>
	);

	return (
		<Fragment>
			{isWarningDialogOpen && (
				<IssueCreateWarningDialog onConfirm={handleConfirm} onCancel={handleCancel} />
			)}
			{/* Using LazyForgeUIExtensions to preload extension list. High chance that we will have the list of
            extension points before user initiate context menu and click dropdown button
         */}
			{contentStatusError && <ErrorDisplay error={contentStatusError} />}
			{contentQueryError && <ErrorDisplay error={contentQueryError} />}
			<LazyForgeUIExtensions
				moduleType={FORGE_MODULE_CONTEXT_MENU}
				render={(_apps, _loading, error) => {
					if (error && !isErrorMarkedAsHandled(error)) {
						return <ErrorDisplay error={error} />;
					}
					return null;
				}}
			/>
			{forgeExtension && activeSelection && !show && (
				<ForgeAction
					content={content.current}
					contentId={contentId}
					forgeExtension={forgeExtension}
					activeSelection={activeSelection}
					onClose={() => {
						setActionStates((prevState) => ({
							...prevState,
							forgeExtension: null,
						}));
					}}
				/>
			)}

			{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
			{fg('kd_definitions_confluence_enable_platform')
				? isReadingAidsOpen &&
					activeSelection &&
					!show && (
						<DefinitionAction
							contentId={contentId}
							contentType={contentType}
							onClose={() => {
								setActionStates((prevState) => ({
									...prevState,
									isReadingAidsOpen: false,
								}));
							}}
							selectedText={activeSelection.selectedText}
							selectionRect={selectionRect}
						/>
					)
				: isReadingAidsOpen &&
					activeSelection &&
					!show && (
						<ReadingAidsPopup
							selectedText={activeSelection.selectedText}
							selectionRange={activeSelection.selectedRange}
							selectionRect={selectionRect}
							isReadingAidsOpen={isReadingAidsOpen}
							onClose={() => {
								void setDefineDefinitionOpen({ defineDefinitionOpen: false });

								setActionStates((prevState) => ({
									...prevState,
									isReadingAidsOpen: false,
								}));
							}}
							contentId={contentId}
							popupPortalContainerId={popupPortalContainerId}
							onResult={(result) => {
								if (readingAidsChangeboardingStep === 'idle') {
									if (result?.nlpSearch?.errorState?.startsWith('NO_ANSWER')) {
										setActionStates((prevState) => ({
											...prevState,
											isReadingAidsOpen: false,
											readingAidsChangeboardingStep: 'modal-undefined',
										}));
									} else {
										stopReadingAidsChangeboarding();
									}
								}
							}}
						/>
					)}

			<ModalTransition>
				{readingAidsChangeboardingStep.startsWith('modal') ? (
					<ReadingAidsChangeboardingModal
						onClose={() => {
							setActionStates((prevState) => ({
								...prevState,
								readingAidsChangeboardingStep: 'disabled',
							}));
							void stopReadingAidsChangeboarding();
						}}
						onNextClick={() => {
							setActionStates((prevState) => ({
								...prevState,
								readingAidsChangeboardingStep: 'spotlight',
								show: true,
							}));
						}}
						modalVersion={readingAidsChangeboardingStep as 'modal-undefined' | 'modal-word-count'}
						contentId={contentId}
					/>
				) : null}
			</ModalTransition>

			{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
			{fg('confluence_frontend_spotlight_ads_migration') ? (
				<PopoverProvider>
					<PopoverContent
						isVisible={readingAidsChangeboardingStep === 'spotlight'}
						placement="bottom-start"
						dismiss={() => void stopReadingAidsChangeboarding()}
					>
						{readingAidsChangeboardingStep === 'spotlight' && (
							<ReadingAidsChangeboardingSpotlight
								contentId={contentId}
								onGotItClick={stopReadingAidsChangeboarding}
							/>
						)}
					</PopoverContent>
					{contentSection}
					{popupSection}
				</PopoverProvider>
			) : (
				<>
					<SpotlightTransition>
						{readingAidsChangeboardingStep === 'spotlight' ? (
							<ReadingAidsChangeboardingSpotlight
								contentId={contentId}
								onGotItClick={stopReadingAidsChangeboarding}
							/>
						) : null}
					</SpotlightTransition>
					{contentSection}
					{popupSection}
				</>
			)}
		</Fragment>
	);
};
